import { echo, echoIsConfigured } from '@laravel/echo-vue'
import axios, { type AxiosResponse } from 'axios'
import { SELECTED_DEPARTMENT_STORAGE_KEY, useUserStore } from '@/stores/user'
import { CSRF_ENDPOINT } from '@/data'

/**
 * We create a new axios instance in the root scope of this composable.
 * This way no matter how many instances of the composable are created,
 * they will all share the same axios instance (singleton).
 *
 * As this client will handle 99% of our requests, we can set the baseURL
 * and any other defaults necessary to work with our API here.
 */
const apiClient = axios.create({
  // Resolve bare paths like `api/foo` from the app root rather than the current SPA route.
  baseURL: '/',
  withCredentials: true,
  withXSRFToken: true,
  headers: {
    'X-Requested-With': 'XMLHttpRequest',
  },
})

let interceptorsConfigured = false

export const useApiClient = () => {
  if (!interceptorsConfigured) {
    apiClient.interceptors.request.use((config) => {
      /**
       * If we are in development, we add the XDEBUG_SESSION_START param
       * to the request. This will allow us to debug the API in PHPStorm.
       * We can add .env variable VITE_DISABLE_PHP_STORM_XDEBUG=true if not using PHPStorm
       */
      if (import.meta.env.DEV && !import.meta.env.VITE_DISABLE_PHP_STORM_XDEBUG) {
        config.params = {
          XDEBUG_SESSION_START: 'PHPSTORM',
          ...config.params,
        }
      }

      // Add the selected department ID to every request.
      // On boot, the user store has not been hydrated yet, so fall back
      // to localStorage until the authenticated user response repopulates it.
      const userStore = useUserStore()
      const selectedDepartmentId = userStore.storeCurrentDepartment?.id
        ?? (() => {
          try {
            const storedDepartmentJson = localStorage.getItem(SELECTED_DEPARTMENT_STORAGE_KEY)
            if (!storedDepartmentJson) {
              return null
            }

            const storedDepartment = JSON.parse(storedDepartmentJson) as { id?: number }
            return storedDepartment.id ?? null
          }
          catch {
            return null
          }
        })()

      if (selectedDepartmentId) {
        config.headers = config.headers || {}
        config.headers['X-Department-ID'] = selectedDepartmentId.toString()
      }

      if (echoIsConfigured()) {
        const socketId = echo().socketId()

        if (socketId) {
          config.headers = config.headers || {}
          config.headers['X-Socket-ID'] = socketId
        }
      }

      return config
    })

    apiClient.interceptors.response.use(
      (response): AxiosResponse => response,
      async (error) => {
        const originalRequest = error.config
        /**
         * If CSRF token-mismatch occurs, grab a fresh token and retry the original request.
         */
        if (error.response && error.response.status === 419) {
          await apiClient.get(CSRF_ENDPOINT)
          if (import.meta.env.DEV) {
            console.log('Fetching fresh CSRF token and retrying original request...')
          }
          return apiClient(originalRequest)
        }

        /**
         * If the user is unauthenticated, we redirect them to the login page.
         * However, we don't want to redirect them if they are already on the
         * login page and their login attempt fails (401 response).
         * We quickly check if the request URL contains "/login" to determine
         * if the request is a login attempt.
         */
        if (error.config.url && !error.config.url.includes('/login')) {
          if (error.response && error.response.status === 401) {
            if (import.meta.env.DEV) {
              console.log('User is unauthenticated. Redirecting to login page...')
            }
            window.location.href = '/login'
            return
          }
        }

        return Promise.reject(error)
      },
    )

    interceptorsConfigured = true
  }

  return {
    apiClient,
  }
}
